import { fetchAuthState, login } from "../api.js";
import { escapeHtml } from "./format.js";

/**
 * Blocking login gate.
 *
 * Shown when the server says a password is required and this browser does not
 * have a valid session. Everything behind it stays unbuilt until then, so an
 * unauthenticated page never starts fetching a library it cannot read.
 */
export class LoginGate {
  private readonly element: HTMLElement;
  private resolve: (() => void) | null = null;

  constructor() {
    this.element = document.createElement("div");
    this.element.className = "login-gate";
    this.element.hidden = true;
    this.element.innerHTML = `
      <form class="login-box" autocomplete="on">
        <h2>web-library-dj</h2>
        <label class="login-field">
          <span class="muted">Password</span>
          <input type="password" name="password" autocomplete="current-password" required />
        </label>
        <button type="submit">Sign in</button>
        <div class="login-error bad" role="alert"></div>
      </form>
    `;
    document.body.appendChild(this.element);

    this.element.querySelector("form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submit();
    });
  }

  private input(): HTMLInputElement {
    return this.element.querySelector("input[name=password]") as HTMLInputElement;
  }

  private async submit(): Promise<void> {
    const button = this.element.querySelector("button") as HTMLButtonElement;
    const error = this.element.querySelector(".login-error") as HTMLElement;
    button.disabled = true;
    error.textContent = "";
    try {
      await login(this.input().value);
      this.input().value = "";
      this.hide();
      this.resolve?.();
      this.resolve = null;
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : String(err);
    } finally {
      button.disabled = false;
    }
  }

  show(): void {
    this.element.hidden = false;
    this.input().focus();
  }

  hide(): void {
    this.element.hidden = true;
  }

  /** Resolves once the browser holds a valid session, prompting if needed. */
  async require(): Promise<void> {
    const state = await fetchAuthState();
    if (!state.required || state.authenticated) return;
    this.show();
    await new Promise<void>((resolve) => {
      this.resolve = resolve;
    });
  }

  /** Re-prompt after a session expires mid-session. */
  challenge(): void {
    if (this.element.hidden) this.show();
  }

  reportError(message: string): void {
    const error = this.element.querySelector(".login-error") as HTMLElement;
    error.innerHTML = escapeHtml(message);
  }
}
